import { clsxm } from '@app/styles/clsxm';
import { EmailDomainDeliverabilityRowOutDto } from '@app/swagger-types';

import { EmailCard } from '../../shared/EmailCard';
import { StatusPill } from '../../shared/StatusPill';

// admin-emailbeheer blok B (opdracht §7.1). Every domain shown against its own baseline
// deviation, never against another domain's absolute number - opdracht's own rule ("outlook.com
// op 34% naast icloud.com op 92% zegt niets"). Rows below the volume floor are greyed out, not
// hidden - still worth seeing, just never flagged.
export const EmailDomainDeliverabilityTable: React.FC<{ rows: EmailDomainDeliverabilityRowOutDto[] }> = ({ rows }) => {
  return (
    <EmailCard
      title="Per receiving domain"
      subtitle="Opens and clicks side by side: delivery that stays high while clicks collapse is the spam-folder fingerprint. Deviation is always against that domain's own 90-day baseline - never against another domain."
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-gray-400">
              <th className="py-2 pr-3">Domain</th>
              <th className="py-2 pr-3 text-right">Sent</th>
              <th className="py-2 pr-3 text-right">Delivered</th>
              <th className="py-2 pr-3 text-right">Bounce</th>
              <th className="py-2 pr-3 text-right">Complaint</th>
              <th className="py-2 pr-3 text-right">Opens</th>
              <th className="py-2 pr-3 text-right">Clicks</th>
              <th className="py-2">Signal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.domainGroup}
                className={clsxm(i % 2 === 1 && 'bg-gray-50', row.belowVolumeFloor && 'text-gray-300')}
              >
                <td className="font-mono py-1.5 pr-3">{row.domainGroup}</td>
                <td className="py-1.5 pr-3 text-right">{row.sent.toLocaleString()}</td>
                <Deviation
                  value={row.deliveredPercent}
                  baseline={row.baselineDeliveredPercent}
                  dimmed={row.belowVolumeFloor}
                />
                <Deviation
                  value={row.bouncePercent}
                  baseline={row.baselineBouncePercent}
                  dimmed={row.belowVolumeFloor}
                  inverse
                />
                <Deviation
                  value={row.complaintPercent}
                  baseline={row.baselineComplaintPercent}
                  dimmed={row.belowVolumeFloor}
                  inverse
                />
                <Deviation
                  value={row.openedPercent}
                  baseline={row.baselineOpenedPercent}
                  dimmed={row.belowVolumeFloor}
                />
                <Deviation
                  value={row.clickedPercent}
                  baseline={row.baselineClickedPercent}
                  dimmed={row.belowVolumeFloor}
                  bold
                />
                <td className="py-1.5">
                  {row.belowVolumeFloor ? (
                    <span className="text-gray-300">Too little volume to assess</span>
                  ) : row.clickDropFlag ? (
                    <StatusPill variant="bad">Possible spam filter - delivery normal, clicks gone</StatusPill>
                  ) : (
                    <span className="text-gray-700">Normal</span>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={8} className="py-4 text-center text-gray-400">
                  No mail sent in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 border-l-2 border-accept-medium pl-3 text-[12.5px] text-gray-700">
        <b className="text-gray-800">Why clicks weigh heavier than opens.</b> Privacy protection prefetches images but
        never clicks anything - a click is always a human. That is why the warning fires on clicks, not opens. Domains
        under ~100 sends are never flagged.
      </p>
    </EmailCard>
  );
};

const Deviation: React.FC<{
  value: number;
  baseline?: number | null;
  dimmed?: boolean;
  inverse?: boolean;
  bold?: boolean;
}> = ({ value, baseline, dimmed, inverse, bold }) => {
  // relative change vs. the domain's own baseline, not an absolute percentage-point diff -
  // matches admin-email-mockup_3.html's delta(): a move from 92% to 34% matters more than a
  // move from 2% to 3%, even though the second is a smaller relative step here.
  const relativeDelta =
    baseline !== undefined && baseline !== null && baseline !== 0
      ? Math.round(((value - baseline) / baseline) * 100)
      : null;

  return (
    <td className="py-1.5 pr-3 text-right">
      <span className={clsxm(bold && 'font-bold text-gray-800')}>{value.toFixed(1)}%</span>
      {!dimmed && relativeDelta !== null && Math.abs(relativeDelta) >= 15 && (
        <span
          className={clsxm(
            'ml-1',
            (inverse ? relativeDelta > 0 : relativeDelta < 0) ? 'text-[#b42d2d]' : 'text-accept-text'
          )}
        >
          ({relativeDelta > 0 ? '+' : ''}
          {relativeDelta}%)
        </span>
      )}
    </td>
  );
};
